import type { LucideIcon } from 'lucide-react';

import type { SubHeadingLevel } from './AnalysisSubHeader';
import { AnalysisMarkdown } from './AnalysisMarkdown';
import { SubSectionLabel } from './SubSectionLabel';

interface LabeledContentBlockProps {
  icon: LucideIcon;
  /**
   * Tailwind text-color class for the icon. Sections that colour-code a block still pass
   * one; left off, the icon takes the label's own colour.
   */
  iconClassName?: string;
  title: string;
  /**
   * Heading level for the title. `h2` for a sub-section directly under the section
   * heading; `h3` where it sits inside an already-titled block.
   */
  headingLevel?: SubHeadingLevel;
  /** Single body paragraph. Rendered when `items` is not given. */
  description?: string;
  /** Bulleted entries, each with a colour-matched dot. Takes precedence over `description`. */
  items?: string[];
  /** Background-color class for the list bullets, e.g. `bg-blue-600`. */
  bulletClassName?: string;
}

/**
 * A sub-section led by an icon + title (see {@link SubSectionLabel}), followed by
 * either a single description or a colour-bulleted list. Both bodies are optional.
 */
export const LabeledContentBlock = ({
  icon,
  iconClassName,
  title,
  headingLevel,
  description,
  items,
  bulletClassName,
}: LabeledContentBlockProps) => (
  <div className="flex flex-col gap-2">
    <SubSectionLabel icon={icon} iconClassName={iconClassName} headingLevel={headingLevel}>
      {title}
    </SubSectionLabel>
    {items && items.length > 0 ? (
      <ul className="flex flex-col gap-3">
        {items.map((item, i) => (
          <li key={`${item}-${i}`} className="flex items-start gap-3.5">
            <span className={`mt-[9px] size-1.5 shrink-0 rounded-full ${bulletClassName ?? ''}`} />
            <AnalysisMarkdown text={item} className="text-body-md text-foreground-alt" />
          </li>
        ))}
      </ul>
    ) : (
      description && (
        <AnalysisMarkdown text={description} className="text-body-md text-foreground-alt" />
      )
    )}
  </div>
);
